/**
 * DashboardTabbedNavigation Component
 *
 * Reusable navigation component for Coach and Admin dashboards
 * Supports main tabs and optional nested sub-tabs
 * On mobile: Uses dropdown for section selection
 * On desktop: Uses horizontal tabs
 *
 * @param {Object} props
 * @param {Array} props.sections - Array of section objects: { id, label, subTabs?: [{id, label}] }
 * @param {string} props.activeSection - Current active section id
 * @param {string} props.activeSubTab - Current active sub-tab id (if section has sub-tabs)
 * @param {Function} props.onSectionChange - Callback when section changes: (sectionId) => void
 * @param {Function} props.onSubTabChange - Callback when sub-tab changes: (subTabId) => void
 * @param {boolean} props.isDesktop - Responsive flag for desktop vs mobile styling
 */

import React from 'react';
import SectionDropdown from './SectionDropdown';

const DashboardTabbedNavigation = ({
  sections,
  activeSection,
  activeSubTab,
  onSectionChange,
  onSubTabChange,
  isDesktop,
}) => {
  const currentSection = sections.find((s) => s.id === activeSection);
  const hasSubTabs = currentSection?.subTabs && currentSection.subTabs.length > 0;

  const handleSectionChange = (sectionId) => {
    onSectionChange(sectionId);
    const section = sections.find((s) => s.id === sectionId);
    // If section has sub-tabs, auto-select first sub-tab
    if (section?.subTabs && section.subTabs.length > 0) {
      onSubTabChange(section.subTabs[0].id);
    } else {
      onSubTabChange(null);
    }
  };

  const styles = {
    mainTabsContainer: {
      borderBottom: '2px solid #e0e0e0',
      backgroundColor: 'white',
    },
    mainTabs: {
      display: 'flex',
      gap: '10px',
      padding: isDesktop ? '0 40px' : '0 16px',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    mainTab: {
      flex: isDesktop ? 1 : '0 0 auto',
      padding: isDesktop ? '16px 24px' : '14px 20px',
      border: 'none',
      borderBottom: '3px solid transparent',
      background: 'none',
      cursor: 'pointer',
      fontSize: isDesktop ? '16px' : '14px',
      fontWeight: '600',
      color: '#666',
      transition: 'all 0.2s',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      textAlign: 'center',
    },
    activeMainTab: {
      color: '#007bff',
      borderBottom: '3px solid #007bff',
    },
    subTabsContainer: {
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e0e0e0',
    },
    subTabs: {
      display: 'flex',
      gap: '8px',
      padding: isDesktop ? '0 40px' : '0 16px',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    subTab: {
      flex: isDesktop ? '0 0 auto' : '0 0 auto',
      padding: isDesktop ? '12px 20px' : '10px 16px',
      border: 'none',
      borderBottom: '2px solid transparent',
      background: 'none',
      cursor: 'pointer',
      fontSize: isDesktop ? '14px' : '13px',
      fontWeight: '500',
      color: '#555',
      transition: 'all 0.2s',
      minHeight: '40px',
      whiteSpace: 'nowrap',
    },
    activeSubTab: {
      color: '#007bff',
      borderBottom: '2px solid #007bff',
      fontWeight: '600',
    },
  };

  return (
    <>
      {/* Main Tabs - Desktop: Horizontal tabs, Mobile: Dropdown */}
      {isDesktop ? (
        <div style={styles.mainTabsContainer}>
          <div style={styles.mainTabs}>
            {sections.map((section) => (
              <button
                key={section.id}
                style={{
                  ...styles.mainTab,
                  ...(activeSection === section.id ? styles.activeMainTab : {}),
                }}
                onClick={() => handleSectionChange(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <SectionDropdown
          sections={sections}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
        />
      )}

      {/* Sub-Tabs (if current section has them) */}
      {hasSubTabs && (
        <div style={styles.subTabsContainer}>
          <div style={styles.subTabs}>
            {currentSection.subTabs.map((subTab) => (
              <button
                key={subTab.id}
                style={{
                  ...styles.subTab,
                  ...(activeSubTab === subTab.id ? styles.activeSubTab : {}),
                }}
                onClick={() => onSubTabChange(subTab.id)}
              >
                {subTab.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardTabbedNavigation;
